// MonthlyStackedColumns — delivery outcomes per due month, as stacked columns:
// delivered on time (emerald), delivered late (amber), still overdue (red).
//
// Items count against the month they were DUE, so a column is a month's
// commitments and not a month's activity. An item still open stays in its due
// month and moves from the red segment to amber once it is delivered.
//
// Three deliberate choices:
//   - The y-axis is zero-based and integer-only. The brand rule against
//     forcing zero is written for percentage lines, where a floor reveals the
//     story; on a bar chart a floor misstates magnitude, because the reader
//     compares column heights.
//   - The column stacks the three OUTCOMES only. Items still inside their due
//     date carry no outcome, so in the current month the stack can sit below
//     the month's due total — the tooltip and the breakdown table carry that
//     total, and the view notes the difference explicitly.
//   - Series are named in a visible legend as well as coloured, since colour
//     alone carries nothing for a colour-blind reader, and each segment is
//     separated by a hairline in the card's own surface colour. Adjacent
//     emerald and red measure close to 1:1 for a reader with deuteranopia, so
//     without the gap a stack of two outcomes reads as one block.
//
// Recharts' accessibility layer is switched OFF deliberately. It wraps the
// plot in a focusable role="application" svg, which inside the labelled image
// is a tab stop that announces nothing; the label plus the breakdown table
// below is the accessible path to the same figures.
//
// The plot is a single labelled image. Exact values live in the tooltip and,
// for anyone who cannot reach a hover, in the monthly breakdown table beneath
// it, which is where the spoken label points.

import type { JSX } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "../../../../contexts/ThemeContext";
import {
  TICK_FONT_SIZE,
  getChartSurfaceColour,
  getErrorSeriesColour,
  getGridColour,
  getSuccessSeriesColour,
  getTickColour,
  getTooltipContentStyle,
  getWarningSeriesColour,
  useReducedMotionAwareAnimationDuration,
} from "../../../../lib/charts/chartTokens";
import { formatReportMonthShort } from "../reportFormat";
import { describeDeliveryChart, formatDeliveryTooltip } from "./deliveryTooltip";
import type { DeliveryBucket } from "../../../../types/report";

const CHART_HEIGHT = 240;

export interface DeliveryTooltipCardProps {
  active?: boolean;
  payload?: ReadonlyArray<{ payload: DeliveryBucket }>;
  isDark: boolean;
}

/** Exported so the card can be tested by direct call: Recharts will not
 *  render it under jsdom, and an untested hover card is an untested string. */
export function DeliveryTooltipCard({
  active,
  payload,
  isDark,
}: DeliveryTooltipCardProps): JSX.Element | null {
  // Recharts calls this with no payload before a column is hovered. Once it
  // has one, the entry always carries its datum, so there is no second guard
  // to write here — an unreachable one would only look like caution.
  if (!active || !payload || payload.length === 0) return null;
  const style = getTooltipContentStyle(isDark);
  return (
    <div style={{ ...style, padding: "8px 10px" }}>
      {formatDeliveryTooltip(payload[0].payload)}
    </div>
  );
}

export interface MonthlyStackedColumnsProps {
  /** Ascending by month, gap months included with zero counts, exactly as the
   *  server sends them. Never empty: the composer draws one bucket per window
   *  position whatever the records say. */
  buckets: DeliveryBucket[];
}

export default function MonthlyStackedColumns({
  buckets,
}: MonthlyStackedColumnsProps): JSX.Element {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const animationDuration = useReducedMotionAwareAnimationDuration();

  const gridColour = getGridColour(isDark);
  const tickColour = getTickColour(isDark);
  const surfaceColour = getChartSurfaceColour(isDark);
  const series = [
    { key: "onTime", label: "Delivered on time", colour: getSuccessSeriesColour(isDark) },
    { key: "late", label: "Delivered late", colour: getWarningSeriesColour(isDark) },
    { key: "stillOverdue", label: "Still overdue", colour: getErrorSeriesColour(isDark) },
  ] as const;

  return (
    <div>
      <div
        role="img"
        aria-label={describeDeliveryChart(buckets)}
        data-testid="delivery-monthly-chart"
        style={{ width: "100%", height: CHART_HEIGHT }}
      >
        <ResponsiveContainer>
          <BarChart
            data={buckets}
            margin={{ top: 8, right: 16, bottom: 0, left: 4 }}
            accessibilityLayer={false}
          >
            <CartesianGrid stroke={gridColour} vertical={false} strokeDasharray="0" />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: tickColour, fontSize: TICK_FONT_SIZE }}
              tickFormatter={formatReportMonthShort}
              interval="preserveStartEnd"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: tickColour, fontSize: TICK_FONT_SIZE }}
              width={32}
              allowDecimals={false}
              domain={[0, "auto"]}
            />
            <Tooltip
              content={(props) => (
                <DeliveryTooltipCard
                  {...(props as {
                    active?: boolean;
                    payload?: ReadonlyArray<{ payload: DeliveryBucket }>;
                  })}
                  isDark={isDark}
                />
              )}
              cursor={{ fill: gridColour }}
            />
            {series.map((entry) => (
              <Bar
                key={entry.key}
                dataKey={entry.key}
                stackId="outcome"
                name={entry.label}
                fill={entry.colour}
                stroke={surfaceColour}
                strokeWidth={1}
                isAnimationActive={animationDuration !== 0}
                animationDuration={animationDuration}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p
        data-testid="delivery-chart-legend"
        className="mt-2 flex flex-wrap items-center gap-4 text-caption text-slate-600 dark:text-slate-400"
      >
        {series.map((entry) => (
          <span key={entry.key} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 rounded-sm"
              style={{ background: entry.colour }}
            />
            {entry.label}
          </span>
        ))}
      </p>
    </div>
  );
}
